import {
  Matrix4,
  Plane,
  type ProjectionType,
  type Quaternion,
  Vector3,
} from '@technology/web3d';
import type {
  CrossSectionCamera,
  CrossSectionClipping,
  CrossSectionContourPlanes,
} from './cross-section-controller.types';
import { ProjectionTypeValue } from './web3d-internals';

export const DEFAULT_CROSS_SECTION_DEPTH = 0.3;
const MIN_CROSS_SECTION_DEPTH = 0.01;
export const DEFAULT_CROSS_SECTION_VIEW_SIZE = 8;
const MIN_CROSS_SECTION_VIEW_SIZE = 0.5;
const ZOOM_SPEED_FACTOR = 0.1;
const CAMERA_DISTANCE = 10;

/**
 * Cross-section view state: an orthographic camera looking along the alignment and a
 * clip box keeping only a slab of `depth` meters behind the current alignment point.
 */
export class CrossSectionController {
  readonly #getCamera: () => CrossSectionCamera;
  readonly #getClipping: () => CrossSectionClipping;
  /** Plane through the alignment point, keeping what is behind it. */
  readonly #pointPlane = new Plane(new Vector3(0, 0, -1), 0);
  /** Plane `depth` meters behind the alignment point, keeping what is in front of it. */
  readonly #depthPlane = new Plane(new Vector3(0, 0, 1), 0);
  #depth = DEFAULT_CROSS_SECTION_DEPTH;
  #viewSize = DEFAULT_CROSS_SECTION_VIEW_SIZE;
  #savedFov: number | undefined;
  #savedProjectionType: ProjectionType | undefined;

  constructor(
    getCamera: () => CrossSectionCamera,
    getClipping: () => CrossSectionClipping
  ) {
    this.#getCamera = getCamera;
    this.#getClipping = getClipping;
  }

  public get depth(): number {
    return this.#depth;
  }

  public set depth(depth: number) {
    this.#depth = Math.max(MIN_CROSS_SECTION_DEPTH, depth);
  }

  public get viewSize(): number {
    return this.#viewSize;
  }

  /** Wheel zoom; positive speeds zoom out. */
  public zoom(speed: number): void {
    this.#viewSize = Math.max(
      MIN_CROSS_SECTION_VIEW_SIZE,
      this.#viewSize * (1 + speed * ZOOM_SPEED_FACTOR)
    );
  }

  public resetViewSize(): void {
    this.#viewSize = DEFAULT_CROSS_SECTION_VIEW_SIZE;
  }

  /** Switches to orthographic, saving the projection once so nested enters restore the original. */
  public enterProjection(): void {
    const camera = this.#getCamera();
    this.#savedFov ??= camera.fov;
    this.#savedProjectionType ??= camera.projectionType;
    camera.projectionType = ProjectionTypeValue.ortho;
    camera.orthoSize = this.#viewSize;
  }

  public restoreProjection(): void {
    const camera = this.#getCamera();
    if (this.#savedFov !== undefined) {
      camera.fov = this.#savedFov;
      this.#savedFov = undefined;
    }
    camera.projectionType =
      this.#savedProjectionType ?? ProjectionTypeValue.perspective;
    this.#savedProjectionType = undefined;
    camera.perspectiveViewOffset.set(0, 0);
    camera.updateProjectionMatrix();
  }

  /** Looks at `center` along the alignment, from the side given by `direction`. */
  public placeCamera(
    center: Vector3,
    forwardOnAlignment: Vector3,
    direction: 1 | -1
  ): void {
    const camera = this.#getCamera();
    camera.orthoSize = this.#viewSize;
    camera.position
      .copy(center)
      .addScaledVector(forwardOnAlignment, -direction * CAMERA_DISTANCE);
    camera.lookAt(center);
    camera.updateProjectionMatrix();
    camera.callListeners();
  }

  public updateClipPlanes(point: Vector3, direction: Quaternion): void {
    const xAxis = new Vector3();
    const yAxis = new Vector3();
    const zAxis = new Vector3();
    new Matrix4()
      .compose(point, direction, new Vector3(1, 1, 1))
      .extractBasis(xAxis, yAxis, zAxis);

    this.#depthPlane.setFromNormalAndCoplanarPoint(
      zAxis,
      point.clone().addScaledVector(zAxis, -this.#depth)
    );
    this.#pointPlane.setFromNormalAndCoplanarPoint(
      zAxis.clone().negate(),
      point
    );
    this.removeClipPlanes();
    const clipping = this.#getClipping();
    clipping.clippingPlanes.push(this.#pointPlane, this.#depthPlane);
    clipping.renderer.localClippingEnabled = true;
  }

  /** Removes only this controller's planes; clipping stays on while others remain. */
  public removeClipPlanes(): void {
    const clipping = this.#getClipping();
    const planes = clipping.clippingPlanes;
    for (let i = planes.length - 1; i >= 0; i--) {
      if (planes[i] === this.#pointPlane || planes[i] === this.#depthPlane) {
        planes.splice(i, 1);
      }
    }
    if (planes.length === 0) {
      clipping.renderer.localClippingEnabled = false;
    }
  }

  /** Clip planes ordered from the camera, which looks along `direction`. */
  public contourPlanes(direction: 1 | -1): CrossSectionContourPlanes {
    return direction === 1
      ? { near: this.#depthPlane, far: this.#pointPlane }
      : { near: this.#pointPlane, far: this.#depthPlane };
  }
}
